import React, { useEffect, useState } from 'react';
// Native MUI Components
import {
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
// Native MUI Dialog
import { MuiDialog } from '@/components/ui';
import { Traveller, Trip } from '@/types';
import { Address } from '@/types/address';
import { useToast } from '@/hooks/use-toast';
import { travellerService } from '@/services/travellerService';
import { Apartment } from '@/types/apartment';
import { apartmentService } from '@/services/apartmentService';
import { useI18n } from '@/hooks/useI18n';

interface EditTravellerFormProps {
  traveller: Traveller;
  trip: Trip;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const EditTravellerForm: React.FC<EditTravellerFormProps> = ({ 
  traveller, 
  trip,
  trigger,
  open: externalOpen,
  onOpenChange: externalOnOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [formData, setFormData] = useState<Traveller>(traveller);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const { toast } = useToast();
  const { t } = useI18n();

  // Use external state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  useEffect(() => {
    setFormData(traveller);
  }, [traveller]);

  useEffect(() => {
    if (open && trip.id) {
      const unsubscribe = apartmentService.listenToApartments(trip.id, (fetchedApartments) => {
        setApartments(fetchedApartments);
      });

      return () => {
        unsubscribe();
      };
    }
  }, [open, trip.id]);

  const handleChange = (field: keyof Traveller, value: string | boolean | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field: keyof Address, value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      } as Address
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await travellerService.updateTraveller(trip.id, formData.id, formData);
      toast({
        title: t('common.success'),
        description: t('traveller.form.travellerUpdated'),
      });
      setOpen(false);
    } catch (error) {
      console.error("Error updating traveller:", error);
      toast({
        title: t('traveller.form.errorUpdatingTraveller'),
        description: t('traveller.form.errorUpdatingTravellerDescription'),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Box onClick={() => setOpen(true)}>
        {trigger}
      </Box>
      
      <MuiDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('traveller.form.editTraveller')}
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label={t('traveller.form.tripAssignment')} />
              <Tab label={t('traveller.form.personalInfo')} />
            </Tabs>
          </Box>
          
          {/* Trip Assignment Tab */}
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{t('traveller.form.apartment')}</InputLabel>
                <Select
                  value={formData.apartmentId || ''}
                  onChange={(e) => handleChange('apartmentId', e.target.value)}
                  label={t('traveller.form.apartment')}
                >
                  <MenuItem value="">
                    <em>{t('traveller.form.noApartment')}</em>
                  </MenuItem>
                  {apartments.map((apartment) => (
                    <MenuItem key={apartment.id} value={apartment.id}>
                      {apartment.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('traveller.form.skiPassType')}</InputLabel>
                <Select
                  value={formData.skiPassType || ''}
                  onChange={(e) => handleChange('skiPassType', e.target.value)}
                  label={t('traveller.form.skiPassType')}
                >
                  <MenuItem value="">
                    <em>{t('traveller.form.noSkiPass')}</em>
                  </MenuItem>
                  {trip.skiPasses?.map((skiPass) => (
                    <MenuItem key={skiPass.id} value={skiPass.id}>
                      {skiPass.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('traveller.form.transportation')}</InputLabel>
                <Select
                  value={formData.transportationType || ''}
                  onChange={(e) => handleChange('transportationType', e.target.value)}
                  label={t('traveller.form.transportation')}
                >
                  <MenuItem value="">
                    <em>{t('traveller.form.noTransportation')}</em>
                  </MenuItem>
                  {trip.transportationOptions?.map((transport) => (
                    <MenuItem key={transport.id} value={transport.id}>
                      {transport.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Personal Info Tab */}
          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('traveller.form.name')}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label={t('traveller.form.email')}
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label={t('traveller.form.phone')}
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
              <TextField
                fullWidth
                label={t('traveller.form.dateOfBirth')}
                type="date"
                value={formData.dateOfBirth || ''}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isSkiClubMember}
                    onChange={(e) => handleChange('isSkiClubMember', e.target.checked)}
                  />
                }
                label={t('traveller.form.skiClubMember')}
              />

              <TextField
                fullWidth
                label={t('traveller.form.membershipFeeOverride')}
                type="number"
                value={formData.membershipFee ?? ''}
                onChange={(e) => handleChange('membershipFee', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder={t('traveller.form.membershipFeeOverridePlaceholder')}
                helperText={t('traveller.form.membershipFeeOverrideHelp', { defaultFee: trip.defaultMembershipFee })}
                inputProps={{ min: 0, step: 1.00 }}
              />
              
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                {t('traveller.form.address')}
              </Typography>
              <TextField
                fullWidth
                label={t('traveller.form.street')}
                value={formData.address?.street || ''}
                onChange={(e) => handleAddressChange('street', e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('traveller.form.city')}
                  value={formData.address?.city || ''}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={t('traveller.form.postalCode')}
                  value={formData.address?.postalCode || ''}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                />
              </Box>
              <TextField
                fullWidth
                label={t('traveller.form.country')}
                value={formData.address?.country || ''}
                onChange={(e) => handleAddressChange('country', e.target.value)}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="contained">
              {t('traveller.form.saveChanges')}
            </Button>
          </Box>
        </Box>
      </MuiDialog>
    </>
  );
};

export default EditTravellerForm;
